/**
 * <TutorCompleteProfilePage> — « Compléter mon profil tuteur », le parcours en dix étapes (page).
 *
 * L'ancien `CompleteTutorProfileStepper` (route `tutor/complete-profile`), ouvert de la tâche
 * « Complète ton profil de tuteur » de l'accueil et de « Profil » dans les paramètres. Ses étapes,
 * dans son ordre (`TUTOR_PROFILE_STEPS`) :
 *  1. missions fixes — Oui / Non ;  2. missions de remplacement — Oui / Non ;
 *  3. ses créneaux (l'éditeur de « Ma dispo », posé par l'écran : `availabilityEditor`), avec le
 *     conseil « Indique un maximum de créneaux » tant que la semaine est trop mince ;
 *  4. ses spécialités ;  5. ses langues étrangères et leur niveau (`<TutorLanguageList>`, celui de la candidature) ;
 *  6. littéraire, scientifique ou les deux ;  7. les classes qu'il accompagne — parmi celles que
 *     l'équipe a approuvées quand elle en a approuvé, et la question le dit ;
 *  8. la facturation (`<TutorBillingStep>` : le statut et le SIRET ; l'IBAN se donne à Stripe) ;  9. « Personnalise ton profil » et l'aperçu de sa fiche
 *     (`<TutorPersonalizeStep>`) ;  10. la charte (`<TutorCharterStep>`).
 * Puis l'accusé, « Tes informations ont bien été enregistrées » (l'écran de félicitations générique
 * d'aujourd'hui).
 *
 * Une étape, un seul en-tête (`<Header steps>`) : la question en titre, le retour, la croix, la
 * barre de progression. Une action par étape, en pied : « Suivant », « Terminer » à la dernière,
 * actif seulement une fois la charte acceptée. Avant « Suivant » des créneaux, l'écran ouvre la
 * feuille d'engagement de « Ma dispo » : elle appartient à l'éditeur de « Ma dispo », pas ici.
 *
 * Contrôlée : l'étape, le brouillon, les erreurs et l'envoi arrivent en props ; chaque réponse
 * remonte par `onChange` (un morceau du brouillon).
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { DelayedSkeleton } from "../../atoms/Skeleton/DelayedSkeleton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { Header } from "../../molecules/Header"
import { TutorBillingStep } from "../../organisms/TutorBillingStep"
import { TutorCharterStep } from "../../organisms/TutorCharterStep"
import { TutorChoiceStep } from "../../organisms/TutorChoiceStep"
import { TutorLanguageList } from "../../organisms/TutorLanguageList"
import { TutorPersonalizeStep } from "../../organisms/TutorPersonalizeStep"
import { TutorWeekEditorSkeleton } from "../../organisms/TutorWeekEditor"
import { PageTemplate } from "../../templates/PageTemplate"
import { TUTOR_FORM_WIDTH } from "../TutorAvailabilityPage/TutorAvailabilityPage"
import {
    type UITutorProfileCatalogs,
    type UITutorProfileDraft,
    type UITutorProfilePreview,
} from "../../../ports/primary/ui/types/tutorCompte"
import { TutorCompleteProfileSkeleton } from "./TutorCompleteProfileSkeleton"
import { TUTOR_PROFILE_STEPS, type TutorProfileStep } from "./tutorProfileSteps"

export { type TutorProfileStep } from "./tutorProfileSteps"

/** Ce qui peut porter une erreur : un champ du brouillon, ou les créneaux. */
export type TutorProfileField = keyof UITutorProfileDraft | "availability"

export type TutorCompleteProfilePageProps = {
    /** L'étape courante, ou `done` une fois le profil enregistré. */
    step: TutorProfileStep | "done"
    draft: UITutorProfileDraft
    catalogs: UITutorProfileCatalogs
    /** L'équipe a approuvé des classes : la question des classes le dit, et la liste s'y borne. */
    approvedClasses?: boolean
    preview: UITutorProfilePreview
    /** L'éditeur de « Ma dispo », posé par l'écran sur l'étape des créneaux. */
    availabilityEditor: React.ReactNode
    /**
     * La semaine de l'étape des créneaux : `loading`, la forme de l'éditeur ; `failed`, la phrase et
     * « Réessayer » ; `ready` (défaut), l'éditeur.
     */
    availabilityStatus?: "loading" | "failed" | "ready"
    onRetryAvailability?: () => void
    /** La semaine est trop mince : le conseil sous l'éditeur. */
    availabilityAdvice?: boolean
    onChange: (patch: Partial<UITutorProfileDraft>) => void
    /**
     * Les erreurs, par champ du brouillon — le contrat des formulaires du DS (`errors`). Une étape à
     * une réponse montre celle de son champ sous ses réponses (« Champ obligatoire », « Veuillez
     * sélectionner le niveau… ») ; `availability` porte celle des créneaux (« Ajoute au moins un
     * créneau »), qui ne sont pas dans le brouillon.
     */
    errors?: Partial<Record<TutorProfileField, string>>
    onNext: () => void
    onBack: () => void
    onClose: () => void
    /** La page de la charte, lue dans l'étape. */
    charterUrl: string
    /** L'envoi du profil est parti (« Terminer ») : le bouton tourne, plus rien ne se touche. */
    submitting?: boolean
    submitError?: string | null
    /** Après l'accusé. */
    onDone: () => void
    /** Le compte n'est pas encore là : les réponses déjà données ne sont pas encore pré-remplies. */
    loading?: boolean
    testID?: string
}

/** La colonne de « Ma dispo » : la semaine en six colonnes y tient sans couper ses heures. */
const PROFILE_WIDTH = TUTOR_FORM_WIDTH

const YES_NO = [
    { id: "yes", label: "Oui" },
    { id: "no", label: "Non" },
]

const TUTOR_PROFILE_TEXTS = {
    fixedMissions: "Es-tu prêt·e à réaliser des missions fixes de tutorat ?",
    fixedMissionsHint: "Tu retrouveras tes élèves chaque semaine aux mêmes horaires",
    replaceMissions: "Es-tu prêt·e à réaliser des missions de remplacement ?",
    replaceMissionsHint: "Tu seras appelé·e à remplacer ponctuellement d'autres tuteurs",
    availability: "Ajoute tes créneaux disponibles",
    availabilityHint: "Indique quand et à quels créneaux tu peux prendre de nouveaux élèves.",
    availabilityFailed: "Impossible de charger tes créneaux.",
    retry: "Réessayer",
    availabilityAdvice:
        "Indique un maximum de créneaux où tu es disponible pour donner cours, pour maximiser tes chances.",
    specialties: "Quelles sont tes matières de spécialités ?",
    languages: "Quelles sont les langues étrangères que tu connais ?",
    relevantSubjects: "Tu te sens plutôt littéraire, scientifique ou les deux ?",
    classes: "Jusqu'à quel niveau te sens-tu capable d'accompagner des élèves sur tes matières de prédilection ?",
    approvedClasses: "Quelles classes souhaites-tu accompagner parmi celles approuvées ?",
    billing: "Indique tes informations de facturation",
    personalize: "Personnalise ton profil",
    charter: "Lis et approuve la charte d'engagement du tuteur",
    next: "Suivant",
    finish: "Terminer",
    saved: "Effectué",
    savedDetail: "Tes informations ont bien été enregistrées",
    done: "Continuer",
}

const T = TUTOR_PROFILE_TEXTS

const toggle = (ids: string[], id: string) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])
const yesNo = (value: boolean | null) => (value == null ? [] : [value ? "yes" : "no"])

/** Les trois gestes de `<TutorLanguageList>`, rendus en morceaux du brouillon. */
function languageHandlers(
    draft: UITutorProfileDraft,
    catalogs: UITutorProfileCatalogs,
    onChange: TutorCompleteProfilePageProps["onChange"]
) {
    const languages = draft.languages
    return {
        onAdd: (id: string) => {
            const name = catalogs.languages.find((language) => language.id === id)?.name ?? id
            onChange({ languages: [...languages, { id, name, level: null }] })
        },
        onRemove: (id: string) => onChange({ languages: languages.filter((item) => item.id !== id) }),
        onChangeLevel: (id: string, level: UITutorProfileDraft["languages"][number]["level"]) =>
            onChange({ languages: languages.map((item) => (item.id === id ? { ...item, level } : item)) }),
    }
}

/** L'erreur sous une étape, à l'encre d'erreur. */
function StepError(props: { message?: string; center?: boolean }) {
    if (props.message == null || props.message === "") return null
    return (
        <Text size="text-md" color="danger-strong" center={props.center}>
            {props.message}
        </Text>
    )
}

export function TutorCompleteProfilePage(props: TutorCompleteProfilePageProps) {
    const {
        step,
        draft,
        catalogs,
        approvedClasses = false,
        preview,
        availabilityEditor,
        availabilityStatus = "ready",
        onRetryAvailability,
        availabilityAdvice = false,
        onChange,
        errors = {},
        onNext,
        onBack,
        onClose,
        charterUrl,
        submitting = false,
        submitError,
        onDone,
        loading = false,
        testID,
    } = props

    if (step === "done") {
        return (
            <PageTemplate
                testID={testID}
                header={<Header title="Compléter mon profil tuteur" steps={{ step: 1, total: 1, onClose }} />}
                padding="4"
                maxContentWidth={PROFILE_WIDTH}
                footer={<Button label={T.done} fullWidth onPress={onDone} />}
            >
                <FlowOutcome iconName="check" tone="success" message={T.saved} detail={T.savedDetail} center />
            </PageTemplate>
        )
    }

    const index = TUTOR_PROFILE_STEPS.indexOf(step)
    const isLast = index === TUTOR_PROFILE_STEPS.length - 1
    const title = step === "classes" && approvedClasses ? T.approvedClasses : T[step]

    let body: React.ReactNode
    switch (step) {
        case "fixedMissions":
        case "replaceMissions":
            body = (
                <TutorChoiceStep
                    description={step === "fixedMissions" ? T.fixedMissionsHint : T.replaceMissionsHint}
                    options={YES_NO}
                    presentation="cards"
                    multiple={false}
                    selectedIds={yesNo(draft[step])}
                    onToggle={(id) => onChange({ [step]: id === "yes" })}
                    error={errors[step]}
                    disabled={submitting}
                />
            )
            break
        case "availability":
            body = (
                <Stack gap="3">
                    <Text color="text-muted">{T.availabilityHint}</Text>
                    {availabilityStatus === "loading" ? (
                        <DelayedSkeleton>
                            <TutorWeekEditorSkeleton />
                        </DelayedSkeleton>
                    ) : availabilityStatus === "failed" ? (
                        <Stack gap="3" align="center">
                            <Banner message={T.availabilityFailed} color="danger" icon="exclamation-circle" tinted />
                            {onRetryAvailability != null && (
                                <Button label={T.retry} variant="outline" onPress={onRetryAvailability} />
                            )}
                        </Stack>
                    ) : (
                        availabilityEditor
                    )}
                    {availabilityStatus === "ready" && availabilityAdvice && (
                        <Banner message={T.availabilityAdvice} color="warning" icon="info-circle" tinted />
                    )}
                    <StepError message={errors.availability} />
                </Stack>
            )
            break
        case "specialties":
            body = (
                <TutorChoiceStep
                    subjects={catalogs.specialtySubjects}
                    selectedIds={draft.specialtySubjectIds}
                    onToggle={(id) => onChange({ specialtySubjectIds: toggle(draft.specialtySubjectIds, id) })}
                    error={errors.specialtySubjectIds}
                    disabled={submitting}
                />
            )
            break
        case "languages":
            body = (
                <Stack gap="3">
                    <TutorLanguageList
                        languages={draft.languages}
                        available={catalogs.languages}
                        {...languageHandlers(draft, catalogs, onChange)}
                        showErrors={errors.languages != null && errors.languages !== ""}
                    />
                    <StepError message={errors.languages} center />
                </Stack>
            )
            break
        case "relevantSubjects":
            body = (
                <TutorChoiceStep
                    options={catalogs.relevantSubjects}
                    presentation="cards"
                    selectedIds={draft.relevantSubjectIds}
                    onToggle={(id) => onChange({ relevantSubjectIds: toggle(draft.relevantSubjectIds, id) })}
                    error={errors.relevantSubjectIds}
                    disabled={submitting}
                />
            )
            break
        case "classes":
            body = (
                <TutorChoiceStep
                    sections={catalogs.classSections}
                    selectedIds={draft.relevantClassIds}
                    onToggle={(id) => onChange({ relevantClassIds: toggle(draft.relevantClassIds, id) })}
                    error={errors.relevantClassIds}
                    disabled={submitting}
                />
            )
            break
        case "billing":
            body = (
                <TutorBillingStep
                    isAutoEntrepreneur={draft.isAutoEntrepreneur}
                    onToggleAutoEntrepreneur={(next) => onChange({ isAutoEntrepreneur: next })}
                    siret={draft.siret}
                    onChangeSiret={(siret) => onChange({ siret })}
                    siretError={errors.siret}
                    disabled={submitting}
                />
            )
            break
        case "personalize":
            body = (
                <TutorPersonalizeStep
                    studyInstitution={draft.studyInstitution}
                    studyField={draft.studyField}
                    sloganId={draft.sloganId}
                    slogans={catalogs.slogans}
                    onChange={(field, value) => onChange({ [field]: value })}
                    preview={preview}
                    errors={errors}
                    disabled={submitting}
                />
            )
            break
        case "charter":
            body = (
                <TutorCharterStep
                    charterUrl={charterUrl}
                    accepted={draft.charterAccepted}
                    onToggleAccepted={(next) => onChange({ charterAccepted: next })}
                    error={errors.charterAccepted}
                    disabled={submitting}
                />
            )
            break
    }

    const footer = (
        <Stack gap="2">
            {isLast && submitError != null && submitError !== "" && (
                <Banner message={submitError} color="danger" icon="exclamation-circle" tinted />
            )}
            <Button
                label={isLast ? T.finish : T.next}
                fullWidth
                disabled={isLast && !draft.charterAccepted}
                loading={isLast && submitting}
                onPress={onNext}
            />
        </Stack>
    )

    return (
        <PageTemplate
            testID={testID}
            header={
                <Header title={title} steps={{ step: index + 1, total: TUTOR_PROFILE_STEPS.length, onBack, onClose }} />
            }
            loading={loading}
            skeleton={<TutorCompleteProfileSkeleton />}
            padding="4"
            maxContentWidth={PROFILE_WIDTH}
            footer={loading ? undefined : footer}
        >
            {body}
        </PageTemplate>
    )
}
